import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Api } from "../../common/service/Api";
import {
  CreateOrganizationManagedAdNetworkDto,
  UpdateOrganizationManagedAdNetworkDto,
} from "../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { OrganizationPublicId, UUIDString } from "../../common/service/server/Types";

const QUERY_KEY = "managedAdNetworks";

export const useManagedAdNetworks = (organizationId: OrganizationPublicId) => {
  return useQuery(
    [QUERY_KEY, organizationId],
    () => Api.managedAdNetworks.getAllManagedAdNetworks(organizationId),
    {
      enabled: !!organizationId,
    },
  );
};

export const useManagedAdNetworksByManager = (
  organizationId: OrganizationPublicId,
  managerId: number,
) => {
  return useQuery(
    [QUERY_KEY, organizationId, "manager", managerId],
    () => Api.managedAdNetworks.getNetworksByManager(organizationId, managerId),
    {
      enabled: !!organizationId && !!managerId,
    },
  );
};

export const useManagedAdNetworksByNetwork = (
  organizationId: OrganizationPublicId,
  networkId: number,
) => {
  return useQuery(
    [QUERY_KEY, organizationId, "network", networkId],
    () => Api.managedAdNetworks.getManagersByNetwork(organizationId, networkId),
    {
      enabled: !!organizationId && !!networkId,
    },
  );
};

export const useCreateManagedAdNetwork = (organizationId: OrganizationPublicId) => {
  const queryClient = useQueryClient();

  return useMutation(
    (dto: CreateOrganizationManagedAdNetworkDto) =>
      Api.managedAdNetworks.createManagedAdNetwork(organizationId, dto),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY, organizationId]);
      },
    },
  );
};

export const useUpdateManagedAdNetwork = (organizationId: OrganizationPublicId) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      managedAdNetworkId,
      dto,
    }: {
      managedAdNetworkId: UUIDString;
      dto: UpdateOrganizationManagedAdNetworkDto;
    }) => Api.managedAdNetworks.updateManagedAdNetwork(organizationId, managedAdNetworkId, dto),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY, organizationId]);
      },
    },
  );
};

export const useDeleteManagedAdNetwork = (organizationId: OrganizationPublicId) => {
  const queryClient = useQueryClient();

  return useMutation(
    (managedAdNetworkId: UUIDString) =>
      Api.managedAdNetworks.deleteManagedAdNetwork(organizationId, managedAdNetworkId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY, organizationId]);
      },
    },
  );
};

const DATA_RETRIEVED_VIA_QUERY_KEY = "dataRetrievedVia";

/** Access-only "Data retrieved via" links (DES-43), kept out of the MANAGED relations every other hook here reads. */
export const useDataRetrievedVia = (organizationId: OrganizationPublicId) => {
  return useQuery(
    [DATA_RETRIEVED_VIA_QUERY_KEY, organizationId],
    () => Api.managedAdNetworks.getDataRetrievedVia(organizationId),
    {
      enabled: !!organizationId,
    },
  );
};

export const useSetDataRetrievedVia = (organizationId: OrganizationPublicId) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ networkId, managerId }: { networkId: string; managerId: string | null }) =>
      Api.managedAdNetworks.setDataRetrievedVia(organizationId, networkId, managerId),
    {
      onSettled: () => {
        queryClient.invalidateQueries([DATA_RETRIEVED_VIA_QUERY_KEY, organizationId]);
      },
    },
  );
};

export const useDeleteManagedAdNetworkByIds = (
  organizationId: OrganizationPublicId,
  options?: { syncSurvey?: boolean },
) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ managerId, networkId }: { managerId: number; networkId: number }) =>
      Api.managedAdNetworks.deleteManagedAdNetworkByIds(
        organizationId,
        managerId,
        networkId,
        options?.syncSurvey,
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY, organizationId]);
      },
    },
  );
};
